import { formatAgo, formatDate } from "./format";

/**
 * TAKE-FOR-WORK logic (spec-artifact-locking §8) — the whole decidable part
 * of the locking UX, pure and DOM-free (compiles under both tsconfigs,
 * unit-tested in test/ui/locking.test.ts). The components are thin wrappers:
 * they render what these functions return and route the intents they name.
 *
 * VOCABULARY (owner-ratified §8): the user never thinks in "locks" or
 * "versions" — the words are „Paimti darbui" / „Grąžinti" / „Atsisakyti" /
 * „Perimti…" / „Atsisiųsti", and the base version the shell remembers is
 * invisible. The wire fields (`lockedBy`/`lockedAt`/`lockNote`) stay in the
 * transport's vocabulary; the translation happens exactly here.
 */

/** The lock triple as it arrives on a row — ABSENT keys mean "free" (the
 * server omits them entirely, src/routes/artifacts.ts serializeArtifactLock),
 * so every field is optional AND nullable: an older/newer serializer that
 * sends explicit nulls reads identically. */
export interface LockFields {
  lockedBy?: string | null;
  lockedAt?: string | null;
  lockNote?: string | null;
}

/** The three visible file states (§8). "mine" vs "other" is what decides
 * whether the file is the user's to return or a colleague's to respect. */
export type LockState = "free" | "mine" | "other";

/** The one intent a lock button emits; the component routes it, the parent
 * fulfils it (take = lock + download + remember, return = upload, abandon =
 * unlock, takeover = unlock-then-lock). */
export type LockActionId = "take" | "return" | "abandon" | "takeover";

export interface LockActionDescriptor {
  id: LockActionId;
  label: string;
  title: string;
}

/**
 * Whose lock is this? Case- and whitespace-insensitive on purpose: the
 * holder is the audit principal (the session user's EMAIL for web
 * sessions, src/http/auth.ts setWebSessionActor), and an identity that
 * differs only in case must never make a user's OWN file look like a
 * colleague's — that would offer „Perimti…" for their own work.
 *
 * `me` unknown (null — a shell with no signed-in identity) degrades to
 * "other": never claim a lock is yours without proof.
 */
export function lockStateOf(row: LockFields, me: string | null): LockState {
  const holder = row.lockedBy?.trim();
  if (!holder) return "free";
  const mine = me?.trim();
  if (mine && holder.toLowerCase() === mine.toLowerCase()) return "mine";
  return "other";
}

/**
 * The action buttons for one row state (§8). `canReturn` is capability
 * DATA, not a host branch: returning means UPLOADING, and the upload
 * affordance only exists in the project view of an upload-capable
 * transport — in a tenant-wide search view there is no single upload
 * target (artifact-browser `showUpload`), so „Grąžinti" is not offered
 * while „Atsisakyti" still is (abandoning never needs bytes).
 */
export function lockActionsFor(state: LockState, canReturn: boolean): LockActionDescriptor[] {
  switch (state) {
    case "free":
      return [
        { id: "take", label: "Paimti darbui", title: "Paimti failą darbui ir atsisiųsti" },
      ];
    case "mine": {
      const actions: LockActionDescriptor[] = [];
      if (canReturn) {
        actions.push({
          id: "return",
          label: "Grąžinti",
          title: "Įkelti pataisytą failą ir grąžinti jį kolegoms",
        });
      }
      actions.push({
        id: "abandon",
        label: "Atsisakyti",
        title: "Atsisakyti failo neįkeliant pakeitimų",
      });
      return actions;
    }
    case "other":
      return [{ id: "takeover", label: "Perimti…", title: "Perimti kolegos paimtą failą" }];
  }
}

/** The row badge (§8: holder, since, note). `null` = free → no badge at
 * all, so a free file's row stays as quiet as it was before locking. */
export interface LockBadge {
  state: Exclude<LockState, "free">;
  text: string;
  /** Tooltip — the note in full plus the exact timestamp behind the age. */
  title: string;
}

export function lockBadge(row: LockFields, me: string | null, now: number): LockBadge | null {
  const state = lockStateOf(row, me);
  if (state === "free") return null;
  const ago = formatAgo(row.lockedAt, now);
  const note = row.lockNote?.trim();
  const who = state === "mine" ? "Paimta jūsų" : `Paimta: ${row.lockedBy}`;
  const text = ago ? `${who} · ${ago}` : who;
  const stamp = formatDate(row.lockedAt);
  return {
    state,
    text: note ? `${text} · „${note}"` : text,
    title: [stamp ? `Paimta ${stamp}` : "", note ? `„${note}"` : ""].filter(Boolean).join(" · "),
  };
}

/**
 * The takeover confirm text (§8: "behind a confirm dialog showing whose
 * work and lock age"). Named and dated on purpose — a takeover is
 * legitimate but audited and visible (spec §10.3), so the user must see
 * exactly whose work they are interrupting before pressing the button.
 */
export function takeoverConfirmText(fileName: string, row: LockFields, now: number): string {
  const ago = formatAgo(row.lockedAt, now);
  const when = ago ? ` ${ago}` : "";
  const note = row.lockNote?.trim();
  const why = note ? ` („${note}")` : "";
  return (
    `Failą „${fileName}" darbui paėmė ${row.lockedBy}${when}${why}. ` +
    "Perimant jo užraktas bus panaikintas, o failas atiteks jums — tai matys visi. Tęsti?"
  );
}

/**
 * Whether an upload must claim the lock FIRST — the SILENT AUTO-LOCK rule
 * (§8, owner verdict 2026-07-28 variant 1): shells always route uploads
 * through the take-for-work path, so an upload of a FREE file locks first,
 * with no warning and no extra click. Invisible infrastructure, not a
 * dialog.
 *
 * "other" also plans a lock — deliberately: the claim is the SERVER's
 * chance to refuse with `lock_held` naming the holder, which is a far
 * better answer than a client-side guess, and `lockArtifact` never steals
 * a colleague's lock (src/domain/locks.ts — takeover is a separate
 * deliberate call). A create needs nothing: a brand-new artifact has no
 * one to collide with.
 */
export type UploadLockPlan = "skip" | "lock";

export function planUploadLock(kind: "create" | "append", state: LockState): UploadLockPlan {
  if (kind === "create") return "skip";
  return state === "mine" ? "skip" : "lock";
}

/**
 * The base version to DECLARE for an append (`metadata.baseVersionNumber`).
 * The remembered base (stored at take time) wins; without one — the silent
 * auto-lock path — the file's CURRENT version at pick time is the honest
 * base. `null` = declare nothing: the domain schema requires a POSITIVE
 * integer (src/domain/versions.ts createVersionMetadataSchema), so an
 * artifact with no versions yet has no base to declare.
 */
export function baseVersionToDeclare(
  remembered: number | null,
  currentVersionNumber: number | null,
): number | undefined {
  const base = remembered ?? currentVersionNumber ?? 0;
  return Number.isInteger(base) && base > 0 ? base : undefined;
}

/** Defensive read of an error envelope's `details` object (the wire is
 * `unknown` — a proxy or an older server may send anything). */
function detailOf(details: unknown, key: string): unknown {
  if (typeof details !== "object" || details === null) return undefined;
  return (details as Record<string, unknown>)[key];
}

function stringDetail(details: unknown, key: string): string | null {
  const value = detailOf(details, key);
  return typeof value === "string" && value.trim() ? value : null;
}

/**
 * LT text for the `lock_held` / `artifact_locked` refusals, composed from
 * the error's DETAILS (src/domain/locks.ts carries lockedBy/lockedAt/
 * lockNote on both). The server's own message is an English prompt written
 * for an LLM ("Next step: …" — §7b.5); the browser user gets the same
 * facts in their own language, with the registry code still shown beside
 * it by the status strip (codes-need-gloss, inverted).
 *
 * Details missing → `null`, and the caller falls back to the generic gloss
 * in errors-lt.ts. Never invent a holder.
 */
export function lockHeldText(details: unknown, now: number): string | null {
  const holder = stringDetail(details, "lockedBy");
  if (!holder) return null;
  const ago = formatAgo(stringDetail(details, "lockedAt"), now);
  const when = ago ? ` ${ago}` : "";
  const note = stringDetail(details, "lockNote");
  const why = note ? ` („${note}")` : "";
  return (
    `Failą darbui jau paėmė ${holder}${when}${why}. ` +
    `Palaukite, kol jis grąžins, arba perimkite failą sąmoningai („Perimti…").`
  );
}

/**
 * LT text for `version_conflict` (§8 "Conflict message"): WHO checked in,
 * WHEN, and the next step. Composed from the details src/domain/locks.ts
 * assertBaseVersionIsCurrent carries.
 *
 * The next step names the FRESH TAKE on purpose. A failed return keeps the
 * remembered base (ui/src/lib/base-memory.ts — forgotten only on a
 * successful check-in, on „Atsisakyti", or when a new take overwrites it),
 * which is the safe choice: forgetting it would let an un-ported retry slip
 * through the guard. The consequence is that a literal "download and retry"
 * re-declares the SAME stale base and 409s again — so the only working
 * recovery is „Paimti darbui" once more, which is what refreshes the base.
 */
export function versionConflictText(details: unknown): string | null {
  const current = detailOf(details, "currentVersionNumber");
  if (typeof current !== "number") return null;
  const author = stringDetail(details, "currentVersionAuthor");
  const stamp = formatDate(stringDetail(details, "currentVersionCreatedAt"));
  const who = author ? ` ${author}` : " kolega";
  const when = stamp ? ` (${stamp})` : "";
  return (
    `Kol dirbote,${who} įkėlė naujesnę versiją${when} — jūsų failas buvo paremtas senesne. ` +
    "Atsisiųskite dabartinę versiją, perkelkite į ją savo pakeitimus ir paimkite failą darbui iš naujo " +
    `(„Paimti darbui") — tik naujas paėmimas atnaujina versiją, nuo kurios dirbate. Tada bandykite dar kartą.`
  );
}

/**
 * The ONE funnel the status strip uses for locking refusals: registry code
 * + details → LT text, or `null` when this error is not a locking one (or
 * carries no usable details) and the generic gloss should win.
 */
export function lockErrorText(code: string, details: unknown, now: number): string | null {
  if (code === "lock_held" || code === "artifact_locked") return lockHeldText(details, now);
  if (code === "version_conflict") return versionConflictText(details);
  return null;
}
